<template>
  <page-wrapper :title="title" :hasPad="true">
    <div class="wrapper">
      <div class="d-flex justify-content-between">
        <sc-button
          v-if="!isViewOnlyByRole"
          :route="{ name: 'ServiceUnavailableDatePage', params: $route.params }"
          class="mb-4"
          variant="secondary"
          >ลงวันไม่ว่าง</sc-button
        >
        <language-select
          v-model="language"
          @change="handleChangeLanguage"
        ></language-select>
      </div>
      <service-editor
        class="editor"
        v-if="!isLoading"
        v-model="form"
        :iconUrl="iconUrl"
        :disabled="isViewOnlyByRole"
        @removeDuration="handleRemoveDuration"
        @addDuration="handleAddDuration"
        @isCheckCombineOption="handleCheckCombineOption"
      ></service-editor>
      <div class="d-flex justify-content-between" v-if="!isViewOnlyByRole">
        <sc-button variant="danger" @click="handleClickRemove">ลบ</sc-button>
        <sc-button variant="primary" @click="handleClickSaveService">บันทึก</sc-button>
      </div>
      <div>
        <service-confirm-material-code
          v-if="isShowConfirmMaterialCode"
          v-model="form"
          @confirmMaterialCode="handleConfirmMaterialCode"
        ></service-confirm-material-code>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import LanguageSelect from "@/lib/components/LanguageSelect";
import ServiceEditor from "../components/ServiceEditor";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { localization } from "@/lib/mapper";
import { isNil, isEmpty, range } from "ramda";
import { DateTime } from "luxon";
import { debounce } from "@/lib/util";
import {
  convertStangToBathAndRoundTwoDecimal,
  convertBathToStangAndRoundTwoDecimal
} from "@/lib/formatter";
import ServiceConfirmMaterialCode from "../components/ServiceConfirmMaterialCode";
import { serviceType } from "@/lib/mapper";

export default {
  components: {
    PageWrapper,
    ScButton,
    ServiceEditor,
    LanguageSelect,
    "service-confirm-material-code": ServiceConfirmMaterialCode
  },
  data() {
    const title = "รายละเอียดบริการ";
    const form = {
      name: "",
      shortDescription: "",
      minimumAdvanceDuration: 5,
      quota: 0,
      cost: null,
      provider: null,
      externalLink: null,
      description: "",
      termsAndConditions: "",
      iconFile: null,
      serviceOptions: null,
      appointDurationSlots: [],
      appointDateConstraints: [],
      serviceOptionCosts: null,
      hasOptionSwitch: true,
      hasEditOptionCost: false,
      sapServiceType: "NORMAL",
      serviceType: serviceType.SERVICE
    };
    const iconUrl = null;
    const isLoading = true;
    const currentQuota = form.quota;
    const permission = JSON.parse(localStorage.getItem("adminRolePermission")) || null;

    return {
      title,
      form,
      iconUrl,
      isLoading,
      language: localization.TH,
      currentQuota,
      isServiceOptionCosts: false,
      isCheckCombineOption: false,
      materialCodeList: [],
      isShowConfirmMaterialCode: false,
      permission
    };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    },
    isViewOnlyByRole() {
      const roles = ["AC","CC"]
      return roles.includes(this.permission.role)
    }
  },
  watch: {
    form(form, oldForm) {
      if (this.form.iconFile === oldForm.iconFile) {
        return;
      }

      this.iconUrl = URL.createObjectURL(this.form.iconFile);
    },
    "form.quota": debounce(function (newQuota) {
      if (
        newQuota > this.currentQuota &&
        newQuota !== this.form.appointDurationSlots.length
      ) {
        this.handleAddAppointDurationSlot(
          newQuota,
          this.currentQuota,
          this.form.appointCost
        );
        this.handleAddDateConstraints(newQuota, this.currentQuota);
      } else if (newQuota < this.currentQuota) {
        this.handleRemoveAppointDurationSlot(newQuota, this.currentQuota);
        this.handleRemoveDateConstraints(newQuota, this.currentQuota);
      }
      this.currentQuota = newQuota;
    }, 0.5)
  },
  created() {
    this.setup();
  },
  methods: {
    async setup() {
      await this.setupData();
      this.isLoading = false;
    },
    async setupData() {
      await this.setupMaterialCode();
      await this.setupForm();
    },
    async setupForm() {
      const data = await this.fetchData();
      const {
        name,
        short_description: shortDescription,
        minimum_advance_duration: minimumAdvanceDuration,
        quota,
        cost,
        provider,
        description,
        terms_and_conditions: termsAndConditions,
        external_link: externalLink,
        icon_url: iconUrl,
        appoint_duration_slots,
        appoint_date_constraints,
        appoint_cost: appointCost,
        packages_by_service: packagesByService,
        options,
        material_code: materialCode,
        material_quantity: materialQuantity,
        sap_service_type: sapServiceType,
        sap_service_recognize_revenue: sapServiceRecognizeRevenue,
        is_vat_registered: isVatRegistered,
        service_type: serviceType
      } = data;

      const appointDateConstraints = appoint_date_constraints.map(
        (appointDateConstraint) => {
          const {
            slot_number: slotNumber,
            reference_date_type: referenceDateType,
            offset,
            type
          } = appointDateConstraint;

          const constraints = offset.values.days;

          return {
            slotNumber,
            referenceDateType,
            constraints,
            type
          };
        }
      );

      const appointCostReadOnly = appointCost && quota >= 1 ? true : false;

      const appointDurationSlots = this.setupAppointDurationSlots(
        appoint_duration_slots,
        appointCost
      );

      const packagesByServiceList = packagesByService.map((item) => {
        const {
          id,
          name,
          price,
          billing_cycle: billingCycle,
          target,
          detail,
          image: imageUrl,
          original_price: originalPrice
        } = item;
        const route = { name: "PackageDetailPage", params: { id } };

        return {
          id,
          imageUrl,
          name,
          price,
          billingCycle,
          target,
          detail,
          originalPrice,
          route
        };
      });

      const serviceOptions = options && options.service_options
        ? options.service_options.map((serviceOption) => {
          const variants = serviceOption.variants.map((variant) => {
            const MaterialDescription = this.findMaterialDescription(variant.material_code)

            let codeDescriptionUnit = null
            let unitName = null
            if (MaterialDescription) {
              codeDescriptionUnit = MaterialDescription.code_description_unit
              unitName = MaterialDescription.unitName
            }

            return {
              ...variant,
              code_description_unit: codeDescriptionUnit,
              unit_name: unitName
            }
          })

          return {
            ...serviceOption,
            variants
          }
        }
        )
        : undefined;

      const serviceOptionCosts = options
        && options.service_option_costs
        && options.service_option_costs.length > 0
        ? options.service_option_costs.map(
          (serviceOptionCost) => {
            const combinationCosts = serviceOptionCost.combination_costs.map((combinationCost) => {
              const appointmentCosts = combinationCost.appointment_costs.map((appointmentCost) => {
                return {
                  ...appointmentCost,
                }
              })

              return {
                ...combinationCost,
                appointment_costs: appointmentCosts
              }
            })

          return {
            ...serviceOptionCost,
            combination_costs: combinationCosts
          }
        }
        )
        : null;

      if (options) {
        this.isServiceOptionCosts = true;
      }

      const hasDeleteOption = options ? true : false;

      const newProvider = {
        id: provider.id,
        isVatRegistered: provider.is_vat_registered,
        logoUrl: provider.logo_url,
        name: provider.name
      }

      this.form = {
        name,
        shortDescription,
        minimumAdvanceDuration: isNil(minimumAdvanceDuration)
          ? 5
          : minimumAdvanceDuration,
        quota,
        cost: convertBathToStangAndRoundTwoDecimal(cost),
        provider: newProvider,
        externalLink,
        description,
        termsAndConditions,
        iconFile: null,
        appointDurationSlots,
        appointDateConstraints,
        appointCost,
        appointCostReadOnly,
        packagesByServiceList,
        serviceOptions,
        serviceOptionCosts,
        hasDeleteOption,
        hasOptionSwitch: true,
        isCanNotEditService: true,
        hasEditOptionCost: false,
        materialCode: materialCode || null,
        materialQuantity: materialQuantity || null,
        sapServiceType: sapServiceType || "NORMAL",
        sapServiceRecognizeRevenue,
        isVatRegistered,
        serviceType
      };

      this.iconUrl = iconUrl;
    },
    async fetchData() {
      const { id } = this.$route.params;

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.services.get(id)
          : await baanRueJaiClient.admin.subscriptions.services.getLocalization(
              id,
              this.language
            );

      return response.data.result;
    },
    setupAppointDurationSlots(appointDurationSlots, appointmentCosts) {
      const newAppointDurationSlots = appointDurationSlots.map((slot, index) => {
        const slotNumber = slot.slot_number;
        const durations = this.setupDurations(slot.durations);
        const defaultDurations = this.setupDurations(slot.default_durations);
        const isDefaultDuration = slot.is_default_duration;
        const appointmentCost = appointmentCosts.find(
          (appoint) => appoint.appointment_no === index + 1
        );
        const appointCost = convertBathToStangAndRoundTwoDecimal(appointmentCost.cost) || null;

        return {
          slotNumber,
          durations,
          defaultDurations,
          isDefaultDuration,
          appointCost
        };
      });

      return newAppointDurationSlots;
    },
    setupDurations(durations) {
      const formattedDurations = durations.map(({ start_at, end_at }) => {
        const startAt = DateTime.fromISO(start_at).toFormat("HH:mm");
        const endAt = DateTime.fromISO(end_at).toFormat("HH:mm");

        return { startAt, endAt };
      });

      return formattedDurations;
    },
    setupAppointDurationSlotsForUpdate(appointDurationSlots) {
      const newAppointDurationSlots = appointDurationSlots.map((slot) => {
        const slot_number = slot.slotNumber;
        const durations = slot.isDefaultDuration
          ? this.setupDurationsForUpdate(slot.defaultDurations)
          : this.setupDurationsForUpdate(slot.durations);
        const appointCost = convertStangToBathAndRoundTwoDecimal(slot.appointCost);

        return {
          slot_number,
          durations,
          appointCost,
        };
      });

      return newAppointDurationSlots;
    },
    setupDurationsForUpdate(durations) {
      const formattedDurations = durations.map(({ startAt, endAt }) => {
        const start_at = DateTime.fromString(startAt, "HH:mm").toFormat(
          "HH:mm:ssZZ"
        );
        const end_at = DateTime.fromString(endAt, "HH:mm").toFormat(
          "HH:mm:ssZZ"
        );

        return { start_at, end_at };
      });

      return formattedDurations;
    },
    hasEmptyDuration() {
      const hasEmptyDuration = this.form.appointDurationSlots.some((slot) =>
        !slot.isDefaultDuration ? this.isEmptyDurations(slot.durations) : false
      );

      return hasEmptyDuration;
    },
    isEmptyDurations(durations) {
      return durations.some(
        (duration) =>
          isEmpty(duration.startAt) ||
          isEmpty(duration.endAt) ||
          isNil(duration.startAt) ||
          isNil(duration.endAt)
      );
    },
    async handleClickSaveService() {
      if (this.form.minimumAdvanceDuration <= 0) {
        alert(
          "ไม่สามารถบันทึกบริการได้เนื่องจากจำนวนวันนัดล่วงหน้าน้อยกว่า 1 วัน"
        );
        return;
      }

      if (this.hasEmptyDuration()) {
        alert("กรุณาใส่ช่วงเวลาให้ครบ");
        return;
      }

      if (!this.form.provider) {
        alert("กรุณาเลือกผู้บริการ");
        return;
      }

      if (this.isCheckCombineOption) {
        alert("มีการเปลี่ยน option กรุณากดสรุปราคาทุน");
        return;
      }

      if (this.isServiceOptionCosts
        && this.form.serviceOptionCosts
        && this.hasEmptyCombineOption()
      ) {
        alert("กรุณากรอกราคาต้นทุน option");
        return;
      }

      if (
        this.form.serviceOptions
        && this.form.hasOptionSwitch
      ) {
        alert("กรุณากดสรุปราคาทุน");
        return;
      }

      this.isShowConfirmMaterialCode = true
    },
    async handleClickSave() {
      try {
        await this.updateService();
        this.$router.push({ name: "ServiceListPage" });
      } catch (err) {
        alert("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    handleChangeLanguage() {
      this.setup();
    },
    async updateService() {
      const { id } = this.$route.params;
      const {
        name,
        shortDescription: short_description,
        minimumAdvanceDuration: minimum_advance_duration,
        quota,
        cost,
        provider,
        externalLink,
        description,
        termsAndConditions: terms_and_conditions,
        iconFile,
        appointDurationSlots,
        appointDateConstraints,
        serviceOptions,
        serviceOptionCosts,
        materialCode,
        materialQuantity,
        sapServiceType: sap_service_type,
        serviceType
      } = this.form;

      const newServiceOptions = serviceOptions
        ? this.setupNewServiceOptions(serviceOptions)
        : undefined

      const newServiceOptionCosts = serviceOptionCosts
        ? this.setupNewServiceOptionCosts(serviceOptionCosts)
        : undefined

      const options = {
        service_options: newServiceOptions,
        service_option_costs: newServiceOptionCosts
      };

      const appoint_duration_slots = this.setupAppointDurationSlotsForUpdate(
        appointDurationSlots
      );
      const appoint_date_constraints = [];
      const provider_id = provider.id;
      const external_link = externalLink;
      const params = {
        name,
        short_description,
        minimum_advance_duration,
        quota,
        cost: convertStangToBathAndRoundTwoDecimal(cost),
        provider_id,
        external_link,
        description,
        terms_and_conditions,
        options,
        // TODO: remove empty list when appoint date constraint and appoint duration ready on production
        appoint_duration_slots,
        appoint_date_constraints: [],
        material_code: materialCode,
        material_quantity: materialQuantity ? +materialQuantity : null,
        sap_service_type,
        service_type: serviceType
      };

      if (iconFile) {
        params.icon = await uploadPublicImage(iconFile);
      }

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.services.put(id, params)
          : await baanRueJaiClient.admin.subscriptions.services.putLocalization(
              id,
              params,
              this.language
            );

      return response.data.result;
    },
    async handleClickRemove() {
      if (!confirm("ยืนยันการลบบริการ")) {
        return;
      }

      try {
        await this.removeService();
        this.$router.push({ name: "ServiceListPage" });
      } catch (err) {
        alert("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    async removeService() {
      const { id } = this.$route.params;

      await baanRueJaiClient.admin.subscriptions.services.delete(id);
    },
    handleRemoveDuration(dataForRemove) {
      const { slotNumber, durationIndex } = dataForRemove;

      this.form.appointDurationSlots
        .find((slot) => slot.slotNumber === slotNumber)
        .durations.splice(durationIndex, 1);
    },
    handleAddDuration(slotNumber) {
      this.form.appointDurationSlots
        .find((slot) => slot.slotNumber === slotNumber)
        .durations.push({ startAt: "", endAt: "" });
    },
    handleAddAppointDurationSlot(newQuota, oldQuota, appointCost) {
      const quotaNumbers = range(oldQuota + 1, newQuota + 1);
      const newAppointDurationSlots = quotaNumbers.map((quota) => {
        const appointmentCost = this.countAppointCost(quota, appointCost);
        const newAppointDurationSlot = {
          slotNumber: quota,
          durations: [
            {
              startAt: "",
              endAt: ""
            },
          ],
          defaultDurations: [
            {
              startAt: "09:00",
              endAt: "12:00"
            },
            {
              startAt: "13:00",
              endAt: "16:00"
            }
          ],
          isDefaultDuration: true,
          appointCost: appointmentCost > 0 ? appointmentCost : null,
          isAppointCostReadOnly: appointmentCost > 0 ? true : false
        };

        return newAppointDurationSlot;
      });

      this.form.appointDurationSlots = this.form.appointDurationSlots.concat(
        newAppointDurationSlots
      );
    },
    countAppointCost(quota, appointmentCost) {
      const appointCost = appointmentCost.map((appointCost) =>
        quota === appointCost.appointment_no
          ? convertBathToStangAndRoundTwoDecimal(appointCost.cost)
          : null
      );
      const cost = appointCost.filter(function (cost) {
        return cost !== null;
      });

      return cost.toString();
    },
    handleRemoveAppointDurationSlot(newQuota, oldQuota) {
      this.form.appointDurationSlots.splice(newQuota, oldQuota - newQuota);
    },
    handleAddDateConstraints(newQuota, oldQuota) {
      const quotaNumbers = range(oldQuota + 1, newQuota + 1);
      const newAppointDateConstraints = quotaNumbers.map((quota) => {
        const newAppointDateConstraint = {
          slotNumber: quota,
          constraints: 0,
          reference_date_type: "PREVIOUS_APPOINTMENT_DATE",
          type: "APPOINTMENT_GAP"
        };

        return newAppointDateConstraint;
      });

      this.form.appointDateConstraints = this.form.appointDateConstraints.concat(
        newAppointDateConstraints
      );
    },
    handleRemoveDateConstraints(newQuota, oldQuota) {
      this.form.appointDateConstraints.splice(newQuota, oldQuota - newQuota);
    },
    hasEmptyAppointmentCost(appointmentCost) {
      const hasEmptyAppointCost = appointmentCost.some((slot) =>
        !slot.appointCost ? true : false
      );

      return hasEmptyAppointCost;
    },
    setupNewServiceOptions(serviceOptions) {
      const newServiceOptions = serviceOptions.map((option) => {
        const variants = option.variants.map((variant) => {
          return {
            variant_id: variant.variant_id,
            is_not_sale: variant.is_not_sale,
            is_out_of_stock: variant.is_out_of_stock,
            title: variant.title,
            material_code: variant.material_code,
            material_quantity: variant.material_quantity
          };
        });

        return {
          id: option.id,
          extra_option: option.extra_option,
          title: option.title,
          variants
        };
      });

      return newServiceOptions;
    },
    setupNewServiceOptionCosts(serviceOptionCosts) {
      const newServiceOptionCosts = serviceOptionCosts.map((optionCost) => {
        const combinationCosts = optionCost.combination_costs.map((combinationCost) => {
          const appointmentCosts = combinationCost.appointment_costs.map((appointmentCosts) => {

            return {
              appoint_no: appointmentCosts.appoint_no,
              cost: parseFloat(appointmentCosts.cost)
            }
          })

          return {
            id: combinationCost.id,
            is_not_sale: combinationCost.is_not_sale,
            is_out_of_stock: combinationCost.is_out_of_stock,
            title: combinationCost.title,
            appointment_costs: appointmentCosts,
            material_code: combinationCost.material_code,
            material_quantity: combinationCost.material_quantity
          }
        })

        return {
          id: optionCost.id,
          is_product: optionCost.is_product,
          title: optionCost.title,
          combination_costs: combinationCosts
        }
      })

      return newServiceOptionCosts;
    },
    hasEmptyCombineOption() {
      let hasOptionCost;

      if (this.form.serviceOptionCosts) {
      hasOptionCost = this.form.serviceOptionCosts.some(
        (appointOption) => {
          const options = appointOption.combination_costs.some(
            (combineOption) => {
              return combineOption.appointment_costs.some(
                (item) => item.cost === null ? true : false
              );
            }
          );

          return options;
        });
      }

      return hasOptionCost;
    },
    handleCheckCombineOption(value) {
      this.isCheckCombineOption = value;
    },
    async setupMaterialCode() {
      const response = await baanRueJaiClient.admin.sap.codeList();
      this.materialCodeList = response.data.result.materials;
    },
    findMaterialDescription(material) {
      if (!this.materialCodeList || this.materialCodeList.length === 0) {
        return null
      };

      const materialSelected = this.materialCodeList.find(
        (materialCode) => materialCode.code === material
      );

      if (!materialSelected) return null;

      const materialSelectedSplitString = materialSelected.code_description_unit.split(" ");
      const unitName = materialSelectedSplitString[materialSelectedSplitString.length - 1];

      return {
        code_description_unit: materialSelected.code_description_unit,
        unitName
      };
    },
    handleConfirmMaterialCode(value) {
      if (value) {
        this.handleClickSave();
      }

      this.isShowConfirmMaterialCode = false
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/services/pages/ServiceDetailPage.vue